/** Super-admin: one tenant — plan/seats (with downgrade preview) + modules. */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../../api/http";
import * as api from "../../api/tenants";
import { TIERS, type ModuleStatus, type Tenant } from "../../api/types";
import { useToast } from "../../components/ToastContext";
import { Badge, Button, Card, Input, Label, Select, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { TenantCredits } from "./TenantCredits";

export function TenantDetailPage() {
  const t = useT();
  const { push } = useToast();
  const { tenantId = "" } = useParams();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [modules, setModules] = useState<ModuleStatus[]>([]);
  const [tier, setTier] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  // "" means "follow the platform default" — the same thing NULL means on
  // the row, kept as a string so an empty box is not read as 0 days.
  const [grace, setGrace] = useState("");
  const [savingGrace, setSavingGrace] = useState(false);

  const reload = useCallback(async () => {
    const [tenantRow, moduleRows] = await Promise.all([
      api.getTenant(tenantId),
      api.listTenantModules(tenantId),
    ]);
    setTenant(tenantRow);
    setTier(tenantRow.subscription_tier);
    setGrace(
      tenantRow.grace_days === null || tenantRow.grace_days === undefined
        ? ""
        : String(tenantRow.grace_days),
    );
    setModules(moduleRows);
  }, [tenantId]);

  useEffect(() => {
    reload().catch(() => push("error", t("common.error.generic")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const onTierChange = async (nextTier: string) => {
    setTier(nextTier);
    setPreview(null);
    if (tenant && nextTier !== tenant.subscription_tier) {
      // Read-only what-if BEFORE saving — the confirm-dialog discipline.
      const p = await api.previewSeats(tenantId, { subscription_tier: nextTier });
      setPreview(p.would_deactivate);
    }
  };

  const onSave = async () => {
    setBusy(true);
    try {
      await api.updateTenant(tenantId, { subscription_tier: tier });
      push("success", t("common.save"));
      setPreview(null);
      await reload();
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const onSaveGrace = async () => {
    setSavingGrace(true);
    try {
      await api.updateTenant(tenantId, {
        grace_days: grace.trim() === "" ? null : Number(grace),
      });
      push("success", t("common.save"));
      await reload();
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setSavingGrace(false);
    }
  };

  const onToggleModule = async (m: ModuleStatus) => {
    try {
      if (m.enabled) {
        await api.disableModule(tenantId, m.code, true);
      } else {
        await api.enableModule(tenantId, m.code);
      }
      setModules(await api.listTenantModules(tenantId));
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    }
  };

  if (!tenant) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{tenant.legal_name}</h1>
        <p className="mt-1 text-muted">
          {tenant.subdomain} · <Badge tone="brand">{tenant.subscription_tier}</Badge>
        </p>
      </div>

      <Card title={t("tenants.tier")}>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-48">
            <Label htmlFor="tier">{t("tenants.tier")}</Label>
            <Select id="tier" value={tier} onChange={(e) => onTierChange(e.target.value)}>
              {TIERS.map((tierOption) => (
                <option key={tierOption} value={tierOption}>
                  {tierOption}
                </option>
              ))}
            </Select>
          </div>
          <Button onClick={onSave} loading={busy} disabled={tier === tenant.subscription_tier}>
            {t("tenants.save")}
          </Button>
        </div>
        {preview !== null && preview > 0 && (
          <p role="alert" className="mt-3 text-sm font-medium text-warning">
            {t("tenants.seatPreview", { n: preview })}
          </p>
        )}
      </Card>

      {/* Payments are bank transfers approved by hand, so the horizon is
          always slightly wrong: money arrives before anyone looks at it.
          Grace is that slack, and the right amount is not a constant — a
          school on 30-day invoice terms needs more than a learner paying
          by UPI in ten seconds. Blank = the platform default. */}
      <Card title={t("tenants.access")}>
        <p className="text-sm text-muted">
          {t("tenants.paidUntil")}:{" "}
          <span className="font-semibold text-ink">
            {tenant.subscription_expires_at
              ? new Date(tenant.subscription_expires_at).toLocaleDateString()
              : t("tenants.neverPaid")}
          </span>
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-48">
            <Label htmlFor="grace">{t("tenants.graceDays")}</Label>
            <Input
              id="grace"
              type="number"
              min={0}
              max={365}
              value={grace}
              placeholder={t("tenants.graceDefault")}
              onChange={(e) => setGrace(e.target.value)}
            />
          </div>
          <Button variant="secondary" onClick={onSaveGrace} loading={savingGrace}>
            {t("tenants.saveGrace")}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted">{t("tenants.graceHelp")}</p>
      </Card>

      <TenantCredits tenantId={tenantId} />

      <Card title={t("tenants.modules")}>
        <ul className="divide-y divide-line/60">
          {modules.map((m) => (
            <li key={m.code} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-semibold text-ink">
                  {m.name} <span className="font-normal text-muted">({m.code})</span>
                </p>
                <p className="text-xs text-muted">{m.description}</p>
              </div>
              {m.always_on ? (
                <Badge tone="neutral">always on</Badge>
              ) : (
                <Button
                  variant={m.enabled ? "secondary" : "primary"}
                  onClick={() => onToggleModule(m)}
                  aria-label={`${m.enabled ? "disable" : "enable"} ${m.code}`}
                >
                  {m.enabled ? "Disable" : "Enable"}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
